/**
 * 类型定义
 */

export interface ConfigData {
  api_key: string;
  base_url?: string;
  model_name: string;
  concurrency?: number;       // 生成标书时的并发数，默认 2
  mermaid_default_view?: 'code' | 'chart';  // mermaid 默认显示模式，默认 'code'
}

export interface ProjectInfo {
  id: number;
  name: string;
  description: string | null;
  status: string;
  current_step: number;
  generation_mode: string;
  outline_descriptive_level: string;
  user_id: number | null;
  owner_name: string | null;
  knowledge_base_ids: number[];
  knowledge_base_names: string[];
  model_config_id: number | null;
  model_config_name: string | null;
  // 企业信息库（支持联合体投标多选）
  company_profile_ids: number[];
  company_profile_names: string[];
  // 项目投标信息
  bid_total_price: string | null;
  bid_duration: string | null;
  project_manager: string | null;
  contact_person: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  bid_agent: string | null;
  other_info: string | null;
  created_at: string | null;
  updated_at: string | null;
  file_count: number;
  outline_count: number;
  content_count: number;
  // 共享字段
  source?: 'owned' | 'shared';
  is_shared?: boolean;
  share_permission?: 'read' | 'edit';
}

// ---------- 企业信息库类型 ----------

export interface CompanyFinancialInfo {
  id: number;
  company_profile_id: number;
  year: string;
  total_assets: string | null;
  net_assets: string | null;
  revenue: string | null;
  net_profit: string | null;
  r_and_d_expenses: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CompanyAttachment {
  id: number;
  company_profile_id: number;
  category: 'qualification' | 'certificate' | 'other';
  description: string | null;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string | null;
  extracted_text_length: number;
  created_at: string | null;
}

export interface CompanyAttachmentDetail extends CompanyAttachment {
  extracted_text: string | null;
}

export interface ProjectAttachment {
  id: number;
  project_id: number;
  description: string | null;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string | null;
  extracted_text_length: number;
  created_at: string | null;
}

export interface ProjectAttachmentDetail extends ProjectAttachment {
  extracted_text: string | null;
}

export interface CompanyProfile {
  id: number;
  user_id: number;
  owner_name: string | null;
  name: string;
  address: string | null;
  legal_representative: string | null;
  legal_representative_id_number: string | null;
  unified_social_credit_code: string | null;
  bank_name: string | null;
  bank_account: string | null;
  company_phone: string | null;
  fax: string | null;
  postal_code: string | null;
  notes: string | null;
  is_default: boolean;
  project_count: number;
  financial_infos: CompanyFinancialInfo[];
  attachment_count: Record<string, number>;
  created_at: string | null;
  updated_at: string | null;
  // 共享字段
  source?: 'owned' | 'shared';
  is_shared?: boolean;
  share_permission?: 'read' | 'edit';
}

export interface TrashItem {
  id: number;
  name: string;
  description: string | null;
  status: string;
  deleted_at: string | null;
  owner_name: string | null;
  file_count: number;
}

export interface TrashListResponse {
  trash_items: TrashItem[];
  total: number;
}

export interface OutlineItem {
  id: string;
  /** 不可变唯一标识，用于内容匹配。插入/删除/重新排序时 id 会变，但 stable_id 不变 */
  stable_id?: string;
  title: string;
  description: string;
  word_count_range?: string;  // 数字范围，如 "800-1500"
  source_requirement_id?: string;
  source_requirement_title?: string;
  section?: string;  // "tech" | "biz"
  children?: OutlineItem[];
  content?: string;
}

export type OutlineMode = 'free' | 'aligned';
export type GenerationMode = 'tech' | 'full';

/** 标书 section 类型 */
export type Section = 'tech' | 'biz';
/** 默认标书 section */
export const DEFAULT_SECTION: Section = 'tech';

export interface OutlineData {
  outline: OutlineItem[];
  project_name?: string;
  project_overview?: string;
}

/** 标书解析结果的各分析类型 */
export type AnalysisResultMap = Record<string, string>;

export const ANALYSIS_TABS = [
  { key: 'overview', label: '项目概述' },
  { key: 'tech_scoring', label: '技术评分标准' },
  { key: 'biz_scoring', label: '商务评分标准' },
  { key: 'tech_requirements', label: '技术要求' },
  { key: 'disqualification', label: '废标项' },
  { key: 'bid_requirements', label: '投标要求' },
  { key: 'commitment', label: '相关承诺函' },
  { key: 'bid_document_format', label: '投标文件格式' },
] as const;

export type AnalysisTypeKey = (typeof ANALYSIS_TABS)[number]['key'];

/** 目录结构生成方式 */
export type OutlineSource =
  | 'ai_free'
  | 'ai_aligned'
  | 'upload_file'
  | 'upload_description_file'
  | 'from_description'
  | 'import_json'
  | 'manual';

/** 目录结构生成方式的显示标签 */
export const OUTLINE_SOURCE_LABELS: Record<OutlineSource, string> = {
  ai_free: 'AI生成(自由模式)',
  ai_aligned: 'AI生成(对齐模式)',
  upload_file: '上传目录文件',
  upload_description_file: '从描述文件生成',
  from_description: '从已有描述生成',
  import_json: '导入JSON',
  manual: '手动编辑',
};

export interface AppState {
  currentStep: number;
  config: ConfigData;
  currentProject: ProjectInfo | null;
  fileContent: string;
  fileName: string;
  projectOverview: string;
  techRequirements: string;
  analysisResults: AnalysisResultMap;
  outlineData: OutlineData | null;
  bizOutlineData: OutlineData | null;
  outlineSource: OutlineSource | null;
  generationMode: GenerationMode;
}

// ---------- 用户认证类型 ----------

export type UserRole = 'admin' | 'enterprise' | 'agent' | 'normal';

export interface UserInfo {
  id: number;
  username: string;
  email?: string | null;
  display_name: string | null;
  role: UserRole;
  wechat_openid?: string | null;
  casdoor_id?: string | null;
  oauth_provider?: string | null;
  is_active: boolean;
  tokens: number;
  total_tokens_used: number;
  points: number;
  total_points_used: number;
  referral_code: string | null;
  referral_reward: number;
  model_config_id: number | null;
  created_at: string | null;
  is_online?: boolean;
  online_seconds?: number;
  last_login_at?: string | null;
  total_online_seconds?: number;
  project_count?: number;
  recharge_amount?: number;
}

/** 推荐码信息 */
export interface ReferralCodeInfo {
  referral_code: string | null;
  referral_reward: number;
}

/** 推荐统计 */
export interface ReferralStats {
  referral_code: string | null;
  referral_reward: number;
  invited_count: number;
  invited_users: Array<{
    id: number;
    username: string;
    display_name: string | null;
    total_points_used: number;
    created_at: string | null;
  }>;
}

/** 推荐码验证结果 */
export interface ReferralCheckResult {
  valid: boolean;
  message: string;
}

export interface UserStats {
  project_count: number;
  tokens_used: number;
  tokens_balance: number;
  user_type: string;
}

/** 积分消费明细流水条目 */
export interface PointsTransactionItem {
  id: number;
  type: 'consume' | 'recharge' | 'referral';
  tokens_used: number;
  points_change: number;
  balance_after: number;
  model_name: string | null;
  description: string | null;
  project_id: number | null;
  project_name: string | null;
  created_at: string;
}

/** 积分明细列表响应 */
export interface PointsTransactionListResponse {
  items: PointsTransactionItem[];
  total: number;
  page: number;
  page_size: number;
  consume_total: number;
  recharge_total: number;
  referral_total: number;
  balance: number;
}

/** 项目消费统计条目 */
export interface ProjectConsumptionStat {
  project_id: number | null;
  project_name: string;
  points_used: number;
  tokens_used: number;
  consume_count: number;
}

/** 项目消费统计响应 */
export interface ProjectConsumptionStatsResponse {
  projects: ProjectConsumptionStat[];
  total_points: number;
  total_tokens: number;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  token: string | null;
  user: UserInfo | null;
}

export interface SystemModelConfig {
  base_url: string;
  api_key: string;
  model: string;
  concurrency?: number;
  mermaid_default_view?: 'code' | 'chart';
  mermaid_export_format?: 'png' | 'svg';
  company_att_text_limit?: number;
  project_att_text_limit?: number;
  kb_max_chunks?: number;
  kb_max_total_chars?: number;
  kb_retrieval_mode?: 'keyword' | 'vector' | 'hybrid';
  llm_temperature?: number;
  prompt_context_tech?: string;
  prompt_context_biz?: string;
  casdoor_enabled?: boolean;
}

export interface ModelConfigItem {
  id: number;
  name: string;
  base_url: string;
  api_key: string;
  model: string;
  is_default: boolean;
  is_shared: boolean;
  is_active: boolean;
  sort_order: number;
  points_ratio: number;
  temperature: number;
  max_context_tokens: number;
  created_at: string | null;
  updated_at: string | null;
}

// ---------- 知识库类型 ----------

export interface KnowledgeBase {
  id: number;
  name: string;
  description: string | null;
  user_id: number;
  owner_name: string | null;
  file_count: number;
  created_at: string | null;
  updated_at: string | null;
  // 共享字段
  source?: 'owned' | 'shared';
  is_shared?: boolean;
  share_permission?: 'read' | 'edit';
}

export interface ImageInfo {
  url: string;
  page?: number | null;
  index: number;
  description: string;
}

export interface KnowledgeBaseFile {
  id: number;
  kb_id: number;
  original_name: string;
  file_type: string;
  file_size: number;
  image_urls?: ImageInfo[] | null;
  created_at: string | null;
}

export interface ChunkInfo {
  index: number;
  char_count: number;
  preview: string;
  content: string;
}

export interface FileChunksResponse {
  file_id: number;
  file_name: string;
  total_chunks: number;
  total_chars: number;
  chunks: ChunkInfo[];
}

// ---------- 共享类型 ----------

/** 实体类型 */
export type EntityType = 'project' | 'knowledge_base' | 'company_profile';

/** 共享权限 */
export type SharePermission = 'read' | 'edit';

/** 共享信息 */
export interface ShareInfo {
  id: number;
  entity_type: EntityType;
  entity_id: number;
  owner_id: number;
  owner_name: string | null;
  shared_with_id: number;
  shared_with_name: string | null;
  permission: SharePermission;
  created_at: string | null;
}

/** 创建共享请求 */
export interface CreateShareRequest {
  entity_type: EntityType;
  entity_id: number;
  username: string;
  permission: SharePermission;
}

/** 检查用户请求 */
export interface CheckUserRequest {
  username: string;
}

/** 检查用户响应 */
export interface CheckUserResponse {
  exists: boolean;
  user_id?: number;
  username?: string;
  display_name?: string;
}

/** 模糊搜索用户项（分享下拉） */
export interface UserSearchItem {
  id: number;
  username: string;
  display_name: string;
}

/** 用户搜索响应 */
export interface UserSearchResponse {
  users: UserSearchItem[];
}

/** 共享列表响应 */
export interface ShareListResponse {
  shares: ShareInfo[];
  total: number;
}

/** 转移所有权请求 */
export interface TransferOwnershipRequest {
  entity_type: EntityType;
  entity_id: number;
  target_username: string;
  cancel_shares?: boolean;
}

/** 转移所有权响应 */
export interface TransferOwnershipResponse {
  success: boolean;
  message: string;
  kept_shares: number;
}

/** 项目/知识库/企业 的共享状态字段 */
export interface WithShareInfo {
  source?: 'owned' | 'shared';
  is_shared?: boolean;
  share_permission?: SharePermission;
}
